<template>
    <div>
        <rkm-form ref="Form">
            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-input
                        :value="data.grupo ? data.grupo.nome : 'Todos'"
                        disabled
                        label="Grupo"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-input
                        :value="data.subgrupo ? data.subgrupo.nome : 'Todos'"
                        disabled
                        label="Subgrupo"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-input-autocomplete
                        :repository="repositories.material"
                        :request-query-string-params="autocompleteParams.material"
                        :response-data-interceptor="data => data.filter(d => d.isAtivo || (!d.isAtivo && d.hasEntradas))"
                        :search-fields="autocompleteFields.material"
                        :validations="validations.material"
                        @select="handleMaterialSelect"
                        label="Material"
                        text-field="descricaoCompleta"
                        uppercase
                        v-model="model.material"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row v-if="model.material && [enums.tipoGrupo.MATERIAL, enums.tipoGrupo.MEDICAMENTO].includes(model.material.grupoTipoId)">
                <rkm-col>
                    <rkm-form-input
                        :value="model.material && model.material.materialBnafarId && `${model.material.materialBnafarDescricao} - ${tipoDescricao(model.material.materialBnafarTipo)}`"
                        disabled
                        label="Cadastro de materiais (CATMAT)"
                    >
                        <rkm-input-group-append slot="append">
                            <rkm-button
                                @click="$refs.ModalMaterialBnafar.open(model.material.id)"
                                color="primary"
                                title="Editar material CATMAT"
                                v-rkm-tooltip.hover
                            >
                                <rkm-icon icon="pen" />
                            </rkm-button>
                        </rkm-input-group-append>
                    </rkm-form-input>
                </rkm-col>
            </rkm-row>

            <rkm-row v-if="model.material && [enums.tipoGrupo.MEDICAMENTO].includes(model.material.grupoTipoId)">
                <rkm-col>
                    <rkm-form-select
                        :disabled="!model.material.materialBnafarId"
                        :options="form.integracaoBnafarMedicamentos"
                        :validations="validations.integracaoBnafarMedicamento"
                        label="Medicamento OBM"
                        show-default-option
                        text-field="medicamentoDescricao"
                        v-model="model.integracaoBnafarMedicamentoId"
                        value-field="medicamentoId"
                    >
                        <rkm-input-group-append slot="append">
                            <rkm-button
                                :disabled="!model.material.materialBnafarId"
                                @click="$refs.ModalIntegracaoBnafarMedicamento.open(model.material.id)"
                                color="primary"
                                title="Cadastrar medicamento OBM"
                                v-rkm-tooltip.hover
                            >
                                <rkm-icon icon="plus" />
                            </rkm-button>
                        </rkm-input-group-append>
                    </rkm-form-select>
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-input
                        :disabled="!model.material"
                        :validations="validations.lote"
                        label="Lote"
                        uppercase
                        v-model="model.lote"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-date-picker
                        :disabled="!model.material"
                        :validations="validations.validadeLote"
                        label="Validade do lote"
                        right
                        v-model="model.validadeLote"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-input
                        :validations="validations.contagem"
                        label="Contagem"
                        number
                        v-model="model.contagem"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <ModalMaterialBnafar
            @save="handleMaterialBnafarSave"
            ref="ModalMaterialBnafar"
        />

        <ModalIntegracaoBnafarMedicamento
            @save="handleIntegracaoBnafarMedicamentoSave"
            ref="ModalIntegracaoBnafarMedicamento"
        />
    </div>
</template>

<script>
// Components.
import ModalIntegracaoBnafarMedicamento from '@/components/cadastro/material/components/integracao-bnafar-medicamento/Modal'
import ModalMaterialBnafar from '@/components/cadastro/material/components/material-bnafar/Modal'

// Enums.
import tipoGrupo from '@/enums/cadastro/grupo/tipoGrupo'

// Mixins.
import { repository, object } from '@/mixins'

// Moment.
import moment from 'moment'

// Vuex.
import { mapActions } from 'vuex'

export default {
    components: {
        ModalIntegracaoBnafarMedicamento,
        ModalMaterialBnafar,
    },
    computed: {
        autocompleteParams() {

            return {
                material: {
                    almoxarifado_check_controle_estoque: true,
                    grupo_id: this.data.grupo ? this.data.grupo.id : null,
                    subgrupo_id: this.data.subgrupo ? this.data.subgrupo.id : null,
                    show_all: true
                },
            }
        },
        autocompleteFields() {

            return {
                material: [
                    { key: 'codigo_material', searchable: true },
                    { key: 'descricao', searchable: true },
                ],
            }
        },
        validations() {

            return {
                material: {
                    required: true,
                },
                lote: {
                    required: true,
                    max: 15,
                },
                validadeLote: {
                    required: true,
                },
                contagem: {
                    required: true,
                    min_value: 1,
                    max: 8,
                },
                integracaoBnafarMedicamento: {
                    required: false,
                },
            }
        },
    },
    created() {

        this.repositories = {
            inventario: this.getConectaSusRepository('almoxarifado.inventario'),
            material: this.getConectaSusRepository('cadastro.material'),
        }
    },
    data: () => ({
        enums: {tipoGrupo},
        form: {
            integracaoBnafarMedicamentos: [],
        },
        model: {
            id: null,
            material: null,
            integracaoBnafarMedicamentoId: null,
            lote: null,
            validadeLote: null,
            contagem: null,
        },
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        handleSave() {
            this.$refs.Form.validate()
                .then(valid => {

                    if (!valid) return

                    if (this.model.material.grupoTipoId === this.enums.tipoGrupo.MEDICAMENTO && this.model.material.materialBnafarId && !this.model.integracaoBnafarMedicamentoId) {
                        this.showWarningConfirmation(
                            'Atenção',
                            'O item selecionado pertence ao grupo do tipo MEDICAMENTOS, mas não possui vínculo com o Medicamento OBM. Deseja continuar?',
                            async () => this.save(),
                        )
                    } else {
                        this.save()
                    }
                })
        },
        save() {
            const validadeLote = moment(this.model.validadeLote)

            const item = {
                id: this.model.material.id,
                codigoMaterial: this.model.material.codigoMaterial,
                descricao: this.model.material.descricaoCompleta,
                grupo: this.model.material.grupo,
                subgrupo: this.model.material.subgrupo,
                isAtivo: this.model.material.isAtivo,
                lote: this.model.lote,
                validadeLote: validadeLote.format('DD/MM/YYYY'),
                estoque: 0,
                contagem: this.model.contagem,
                isNovoLote: true,
                deleted: false,
                aVencerOuVencido: moment.duration(
                    validadeLote.diff(moment(this.data.dataAtual, 'DD/MM/YYYY'))
                ).asDays() < 90,
                integracaoBnafarMedicamentoId: this.model.integracaoBnafarMedicamentoId,
            }

            this.$emit('save', item)
        },
        handleMaterialSelect() {
            this.getIntegracaoBnafarMedicamentos(this.model.material.id)
        },
        getIntegracaoBnafarMedicamentos(materialId) {
            this.setLoading()

            const request = {material_id: materialId}

            this.repositories.inventario.getIntegracaoBnafarMedicamentos(request)
                .then((response) => {
                    this.form.integracaoBnafarMedicamentos = response.data

                    if (this.form.integracaoBnafarMedicamentos.length === 1) {
                        this.model.integracaoBnafarMedicamentoId = this.form.integracaoBnafarMedicamentos[0].medicamentoId
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleMaterialBnafarSave(materialBnafar) {
            this.model.material.materialBnafarId = materialBnafar.id
            this.model.material.materialBnafarCodigo = materialBnafar.codigo
            this.model.material.materialBnafarDescricao = materialBnafar.descricao
            this.model.material.materialBnafarTipo = materialBnafar.tipo
        },
        handleIntegracaoBnafarMedicamentoSave() {
            this.getIntegracaoBnafarMedicamentos(this.model.material.id)
            this.model.integracaoBnafarMedicamentoId = null

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        tipoDescricao(tipo) {
            switch (tipo) {
                case 'B':
                    return 'Básico';
                case 'E':
                    return 'Especializado';
                case 'S':
                    return 'Estratégico';
                case 'O':
                    return 'Outros';
                default:
                    return 'Desconhecido';
            }
        },
    },
    mixins: [ repository, object ],
    props: {
        data: { type: Object, required: true },
    },
    watch: {
        'model.material': function (material) {
            this.form.integracaoBnafarMedicamentos = []
            this.model.integracaoBnafarMedicamentoId = null

            if (!material) {

                this.model.lote = null
                this.model.validadeLote = null
            }
        },
    },
}
</script>